import type {
  DisruptionLink,
  JourneyLeg,
  JourneyPlan,
  LegStop,
  OperatorDetail,
} from '../../../arcticapi/types';
import {
  buildLineDisruptionLink,
  buildStopDisruptionLink,
  mergeDisruptionLinks,
} from '../../base/DisruptionAlert/disruptionLinks';
import { resolveJourneyServiceLabel } from '../../../transportModes';

export type ItineraryStepKind =
  | 'start'
  | 'walk'
  | 'wait'
  | 'boarding'
  | 'alighting'
  | 'end';

interface ItineraryStepBase {
  key: string;
  disruption?: DisruptionLink | null;
}

export interface ItineraryStartStep extends ItineraryStepBase {
  kind: 'start';
  action: 'Leave';
  timeDateTime: string;
}

export interface ItineraryWalkStep extends ItineraryStepBase {
  kind: 'walk';
  action: string;
  toStop?: string;
  toMode?: string;
  isToDestination: boolean;
  walkMinutes: number;
  paces?: number;
}

export interface ItineraryWaitStep extends ItineraryStepBase {
  kind: 'wait';
  action: string;
  atStop?: string;
  waitMinutes: number;
}

export interface ItineraryBoardingStep extends ItineraryStepBase {
  kind: 'boarding';
  action: string;
  service: string;
  lineName?: string;
  headsign?: string;
  lineHref?: string;
  operatorCode?: string | null;
  mode: string;
  stopName?: string;
  stopAtcoCode?: string;
  lineColor?: string;
  timeDateTime: string;
  expectedDepartureDateTime?: string;
  expectedState?: JourneyLeg['expectedState'];
  expectedStateAffectsTransfers?: boolean;
  timetableHref?: string;
  showDeparturesLink: boolean;
  showTimetableLink: boolean;
  operator?: OperatorDetail;
  notes: string[];
}

export interface ItineraryAlightingStep extends ItineraryStepBase {
  kind: 'alighting';
  action: string;
  stopName?: string;
  lineColor?: string;
  timeDateTime: string;
}

export interface ItineraryEndStep extends ItineraryStepBase {
  kind: 'end';
  action: 'Arrive';
  timeDateTime: string;
}

export type ItineraryStep =
  | ItineraryStartStep
  | ItineraryWalkStep
  | ItineraryWaitStep
  | ItineraryBoardingStep
  | ItineraryAlightingStep
  | ItineraryEndStep;

interface BuildItineraryStepsDependencies {
  formatMode: (mode: string) => string;
  getDurationMinutes: (start: string, end: string) => number;
  websiteBaseHref?: string | null;
}

function getStopDisruptionLink(
  websiteBaseHref: string | null | undefined,
  stop: LegStop | undefined,
): DisruptionLink | null {
  return buildStopDisruptionLink(
    websiteBaseHref ?? null,
    stop?.atcoCode,
    stop?.alerts?.length ?? 0,
  );
}

function getLegLineDisruptionLink(
  websiteBaseHref: string | null | undefined,
  leg: JourneyLeg,
): DisruptionLink | null {
  return buildLineDisruptionLink(
    websiteBaseHref ?? null,
    {
      operatorCode: leg.operator?.code ?? leg.line?.operator,
      lineName: leg.lineName ?? leg.line?.name,
      href: leg.line?.href,
    },
    leg.alerts?.length ?? 0,
  );
}

function buildExpandedStepKey(
  legIndex: number,
  kind: ItineraryStepKind,
): string {
  return `${legIndex}-${kind}`;
}

function getLegFromStop(leg: JourneyLeg): string | undefined {
  return leg.stops?.[0]?.commonName;
}

function getLegToStop(leg: JourneyLeg): string | undefined {
  if (!leg.stops || leg.stops.length === 0) {
    return undefined;
  }

  return leg.stops[leg.stops.length - 1]?.commonName;
}

function areSameStop(a?: string, b?: string): boolean {
  if (!a || !b) {
    return false;
  }

  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

function getWalkDestinationName(
  leg: JourneyLeg,
  nextLeg: JourneyLeg | undefined,
  isLastLeg: boolean,
  formatMode: (mode: string) => string,
): {
  toStop?: string;
  toMode?: string;
  isToDestination: boolean;
  action: string;
} {
  if (isLastLeg) {
    return {
      toStop: undefined,
      toMode: undefined,
      isToDestination: true,
      action: 'Walk to your destination',
    };
  }

  if (nextLeg && nextLeg.mode !== 'walk') {
    const toMode = formatMode(nextLeg.mode);

    return {
      toStop: getLegToStop(leg) ?? getLegFromStop(nextLeg),
      toMode,
      isToDestination: false,
      action: `Walk to the ${toMode} stop`,
    };
  }

  const toStop =
    getLegToStop(leg) ?? (nextLeg ? getLegFromStop(nextLeg) : undefined);

  if (toStop) {
    return {
      toStop,
      toMode: undefined,
      isToDestination: false,
      action: `Walk to ${toStop}`,
    };
  }

  return {
    toStop: undefined,
    toMode: undefined,
    isToDestination: false,
    action: 'Walk to your next stop',
  };
}

function buildWalkStep(
  leg: JourneyLeg,
  legIndex: number,
  nextLeg: JourneyLeg | undefined,
  isLastLeg: boolean,
  formatMode: (mode: string) => string,
): ItineraryWalkStep {
  const destination = getWalkDestinationName(
    leg,
    nextLeg,
    isLastLeg,
    formatMode,
  );
  const walkMinutes = getRoundedUpDurationMinutes(
    leg.departureDateTime,
    leg.arrivalDateTime,
  );

  return {
    key: buildExpandedStepKey(legIndex, 'walk'),
    kind: 'walk',
    action: destination.action,
    toStop: destination.toStop,
    toMode: destination.toMode,
    isToDestination: destination.isToDestination,
    walkMinutes,
    paces: leg.paces,
  };
}

function getRoundedUpDurationMinutes(start: string, end: string): number {
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();

  if (Number.isNaN(startTime) || Number.isNaN(endTime) || endTime < startTime) {
    return 0;
  }

  return Math.max(1, Math.ceil((endTime - startTime) / 60_000));
}

function buildWaitStep(
  previousLeg: JourneyLeg,
  currentLeg: JourneyLeg,
  currentLegIndex: number,
  getDurationMinutes: (start: string, end: string) => number,
): ItineraryWaitStep | undefined {
  const fromStop = getLegToStop(previousLeg);
  const toStop = getLegFromStop(currentLeg);

  if (!areSameStop(fromStop, toStop)) {
    return undefined;
  }

  const waitMinutes = getDurationMinutes(
    previousLeg.arrivalDateTime,
    currentLeg.departureDateTime,
  );

  if (waitMinutes <= 0) {
    return undefined;
  }

  return {
    key: buildExpandedStepKey(currentLegIndex, 'wait'),
    kind: 'wait',
    action: fromStop
      ? `Wait ${waitMinutes} min at ${fromStop}`
      : `Wait ${waitMinutes} min`,
    atStop: fromStop,
    waitMinutes,
  };
}

function buildBoardingStep(
  leg: JourneyLeg,
  legIndex: number,
  formatMode: (mode: string) => string,
  websiteBaseHref: string | null | undefined,
): ItineraryBoardingStep {
  const service = resolveJourneyServiceLabel(leg);
  const fromStop = getLegFromStop(leg);
  const boardingStop = leg.stops?.[0];
  const lineName = leg.lineName ?? leg.line?.name;

  return {
    key: buildExpandedStepKey(legIndex, 'boarding'),
    kind: 'boarding',
    action: `Board ${service}`,
    service,
    lineName,
    headsign: leg.headsign,
    lineHref: leg.line?.href,
    operatorCode: leg.operator?.code ?? leg.line?.operator,
    mode: leg.mode,
    stopName: fromStop,
    stopAtcoCode: boardingStop?.atcoCode,
    timeDateTime: leg.departureDateTime,
    lineColor: leg.line?.colors?.background,
    expectedDepartureDateTime: leg.expectedDepartureDateTime,
    expectedState: leg.expectedState,
    expectedStateAffectsTransfers: leg.expectedStateAffectsTransfers,
    timetableHref: leg.timetableLink?.href,
    showDeparturesLink: Boolean(leg.departuresHref),
    showTimetableLink: Boolean(leg.timetableLink?.href),
    operator: leg.operator,
    notes: leg.notes?.map((note) => note.text).filter(Boolean) ?? [],
    disruption: mergeDisruptionLinks(websiteBaseHref ?? null, [
      getLegLineDisruptionLink(websiteBaseHref, leg),
      getStopDisruptionLink(websiteBaseHref, boardingStop),
    ]),
  };
}

function buildAlightingStep(
  leg: JourneyLeg,
  legIndex: number,
  websiteBaseHref: string | null | undefined,
): ItineraryAlightingStep {
  const toStop = getLegToStop(leg);
  const alightingStop = leg.stops?.[leg.stops.length - 1];

  return {
    key: buildExpandedStepKey(legIndex, 'alighting'),
    kind: 'alighting',
    action: toStop ? `Get off at ${toStop}` : 'Get off',
    stopName: toStop,
    timeDateTime: leg.arrivalDateTime,
    lineColor: leg.line?.colors?.background,
    disruption: getStopDisruptionLink(websiteBaseHref, alightingStop),
  };
}

function buildStepsForLeg(
  leg: JourneyLeg,
  legIndex: number,
  legs: JourneyLeg[],
  dependencies: BuildItineraryStepsDependencies,
): ItineraryStep[] {
  const isLastLeg = legIndex === legs.length - 1;
  const nextLeg = legs[legIndex + 1];
  const previousLeg = legs[legIndex - 1];

  if (leg.mode === 'walk') {
    return [
      buildWalkStep(leg, legIndex, nextLeg, isLastLeg, dependencies.formatMode),
    ];
  }

  const steps: ItineraryStep[] = [];

  if (previousLeg && previousLeg.mode !== 'walk') {
    const waitStep = buildWaitStep(
      previousLeg,
      leg,
      legIndex,
      dependencies.getDurationMinutes,
    );
    if (waitStep) {
      steps.push(waitStep);
    }
  }

  steps.push(
    buildBoardingStep(
      leg,
      legIndex,
      dependencies.formatMode,
      dependencies.websiteBaseHref,
    ),
  );
  steps.push(buildAlightingStep(leg, legIndex, dependencies.websiteBaseHref));

  return steps;
}

/**
 * Converts "legs" (travel segments) into user-facing itinerary steps.
 */
export function buildItinerarySteps(
  plan: JourneyPlan,
  dependencies: BuildItineraryStepsDependencies,
): ItineraryStep[] {
  const { legs } = plan;
  const firstLeg = legs[0];
  const lastLeg = legs[legs.length - 1];

  if (!firstLeg || !lastLeg) {
    return [];
  }

  const steps: ItineraryStep[] = [
    {
      key: buildExpandedStepKey(-1, 'start'),
      kind: 'start',
      action: 'Leave',
      timeDateTime: firstLeg.departureDateTime,
    },
  ];

  legs.forEach((leg, legIndex) => {
    steps.push(...buildStepsForLeg(leg, legIndex, legs, dependencies));
  });

  steps.push({
    key: buildExpandedStepKey(legs.length, 'end'),
    kind: 'end',
    action: 'Arrive',
    timeDateTime: lastLeg.arrivalDateTime,
  });

  return steps;
}
